import React, { useRef, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import { toggleFullscreen, togglePictureInPicture } from './videoPlayerUtils';
import VideoPlayerControls from './VideoPlayerControls';
import './CustomVideoPlayer.css';

const EditingVideoPlayer = ({ 
  playerRef, url, playing, volume, onDuration, onProgress, onSeek, onPlay, onPause, onError, className = ''
}) => {
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(playing || false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentVolume, setCurrentVolume] = useState(volume || 1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handlePlayPause = () => {
    if (playerRef.current) {
      const next = !isPlaying;
      setIsPlaying(next);
      if (next) {
        onPlay?.();
      } else {
        onPause?.();
      }
    }
  };

  const handleProgressClick = (e) => {
    if (playerRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newTime = ((e.clientX - rect.left) / rect.width) * duration;
      playerRef.current.seekTo(newTime, 'seconds');
      setCurrentTime(newTime);
      onSeek?.(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = Number.parseFloat(e.target.value);
    setCurrentVolume(newVolume);
    playerRef.current?.getInternalPlayer()?.setVolume?.(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    if (isMuted) { setCurrentVolume(currentVolume || 0.5); setIsMuted(false); }
    else { setCurrentVolume(0); setIsMuted(true); }
  };

  const handleFullscreen = () => toggleFullscreen(containerRef, isFullscreen);

  const handlePictureInPicture = async () => {
    await togglePictureInPicture(playerRef.current?.getInternalPlayer?.());
  };

  const handleSeekArrow = (direction) => {
    const seekAmount = direction === 'right' ? 5 : -5;
    playerRef.current?.seekTo(currentTime + seekAmount, 'seconds');
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      switch (e.key) {
        case ' ': e.preventDefault(); handlePlayPause(); break;
        case 'f': handleFullscreen(); break;
        case 'm': handleMuteToggle(); break;
        case 'ArrowRight': e.preventDefault(); handleSeekArrow('right'); break;
        case 'ArrowLeft': e.preventDefault(); handleSeekArrow('left'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTime, isPlaying, isMuted, currentVolume]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} className={`custom-video-player ${className}`}>
        <ReactPlayer
          ref={playerRef} className="react-player" url={url} playing={playing} volume={volume}
          onDuration={(dur) => { setDuration(dur); onDuration?.(dur); }}
          onProgress={(state) => { setCurrentTime(state.playedSeconds); setIsPlaying(state.played > 0 && state.played < 1); onProgress?.(state); }}
          onSeek={onSeek}
          onPlay={() => { setIsPlaying(true); onPlay?.(); }}
          onPause={() => { setIsPlaying(false); onPause?.(); }}
          onError={onError} width="100%" height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
        <VideoPlayerControls
          isPlaying={isPlaying} currentTime={currentTime} duration={duration}
          volume={currentVolume} isMuted={isMuted} isFullscreen={isFullscreen}
          showMenu={showMenu} setShowMenu={setShowMenu}
          onPlayPause={handlePlayPause} onProgressClick={handleProgressClick}
          onVolumeChange={handleVolumeChange} onMuteToggle={handleMuteToggle}
          onFullscreen={handleFullscreen} onPictureInPicture={handlePictureInPicture}
          onSeekArrow={handleSeekArrow} progressRole="range"
        />
      </div>
    </div>
  );
};

EditingVideoPlayer.propTypes = {
  playerRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  url: PropTypes.string.isRequired,
  playing: PropTypes.bool, volume: PropTypes.number,
  onDuration: PropTypes.func, onProgress: PropTypes.func, onSeek: PropTypes.func,
  onPlay: PropTypes.func, onPause: PropTypes.func, onError: PropTypes.func, className: PropTypes.string,
};

export default EditingVideoPlayer;
